import { z } from 'zod';
import { EventDate, Geo, Issue, Link, LocalDate, Timestamp, Ulid, Venue } from './common.js';
import { CandidateStatus, Category, DecisionOutcome } from './enums.js';
/**
 * Evento così come estratto da una sorgente (adapter strutturato o LLM), prima della normalizzazione.
 * È anche lo schema dell'output strutturato richiesto al compito AI `extract`.
 */
export const ExtractedEvent = z
    .object({
    title: z.string().min(1).max(300),
    dates: z.array(EventDate).min(1),
    /** testo del luogo così come appare nella sorgente, es. "Torre Inserraglio (Nardò)" */
    placeText: z.string().max(300),
    comune: z.string().max(120).optional(),
    frazione: z.string().max(120).optional(),
    venue: Venue.optional(),
    category: Category.optional(),
    description: z.string().max(20000).optional(),
    links: z.array(Link).default([]),
    imageUrl: z.url().optional(),
    priceText: z.string().max(200).optional(),
    organizer: z.string().max(200).optional(),
    cancelled: z.boolean().optional(),
    sourceUrl: z.url().optional(),
    confidence: z
        .object({
        overall: z.number().min(0).max(1),
        fields: z.record(z.string(), z.number().min(0).max(1)).default({}),
    })
        .optional(),
})
    .meta({ id: 'ExtractedEvent' });
/** Evento normalizzato (date verificate, comune risolto nel gazetteer). */
export const NormalizedEvent = z
    .object({
    title: z.string(),
    category: Category,
    comuneId: z.string().optional(),
    frazione: z.string().optional(),
    venue: Venue.optional(),
    geo: Geo.optional(),
    dates: z.array(EventDate).min(1),
    startDate: LocalDate,
    endDate: LocalDate,
    description: z.string().optional(),
    links: z.array(Link).default([]),
    imageUrl: z.url().optional(),
    cancelled: z.boolean().default(false),
})
    .meta({ id: 'NormalizedEvent' });
export const Decision = z
    .object({
    outcome: DecisionOutcome,
    matchEventId: Ulid.optional(),
    /** duplicato di un altro candidato ancora in sospeso (stesso evento da due sorgenti) */
    matchCandidateId: Ulid.optional(),
    scores: z.record(z.string(), z.number()).default({}),
    judge: z
        .object({
        relation: z.enum(['same', 'different', 'other_edition', 'sub_event']),
        confidence: z.number().min(0).max(1),
        reason: z.string().optional(),
    })
        .optional(),
    by: z.string(),
    at: Timestamp,
})
    .meta({ id: 'Decision' });
/** Record simile trovato dalla deduplica (evento o candidato), con i punteggi. */
export const DedupMatch = z
    .object({
    kind: z.enum(['event', 'candidate']),
    id: Ulid,
    title: z.string(),
    startDate: LocalDate,
    endDate: LocalDate,
    score: z.number().min(0).max(1),
    titleScore: z.number().min(0).max(1),
    dateScore: z.number().min(0).max(1),
    status: z.string().optional(),
})
    .meta({ id: 'DedupMatch' });
export const CandidateEnriched = z
    .object({
    description: z.string().max(5000),
    shortDesc: z.string().max(140),
    seoTitle: z.string().max(70),
    seoDescription: z.string().max(170),
    /** solo se la sorgente non la indicava */
    category: Category.optional(),
    model: z.string(),
    promptVersion: z.string(),
    at: Timestamp,
})
    .meta({ id: 'CandidateEnriched' });
/**
 * Locandina o foto dell'evento presa dalla sorgente e salvata nel bucket raw al momento
 * dell'estrazione: all'approvazione diventa l'immagine dell'evento (se non ne ha già una).
 */
export const CandidatePoster = z
    .object({
    /** chiave nel bucket raw (`posters/<data>/<uuid>.<ext>` o `submissions/…` per le segnalazioni) */
    key: z.string(),
    contentType: z.string(),
    /** indirizzo originale (assente per le locandine caricate dal sito) */
    sourceUrl: z.url().optional(),
})
    .meta({ id: 'CandidatePoster' });
/** Evento proposto da una sorgente, prima della decisione. docs/03-modello-dati.md §2.2. */
export const Candidate = z
    .object({
    id: Ulid,
    sourceId: z.string(),
    externalKey: z.string(),
    /** esecuzione che l'ha prodotto */
    runId: Ulid.optional(),
    rawS3Key: z.string().optional(),
    referenceDate: LocalDate,
    extracted: ExtractedEvent,
    normalized: NormalizedEvent.optional(),
    issues: z.array(Issue).default([]),
    confidence: z.number().min(0).max(1),
    embedding: z.string().optional(),
    decision: Decision.optional(),
    /** i record più simili (al massimo 3), per il confronto nel pannello */
    matches: z.array(DedupMatch).default([]),
    /** testi riscritti con l'AI per i candidati nuovi (compito enrich), usati all'approvazione */
    enriched: CandidateEnriched.optional(),
    poster: CandidatePoster.optional(),
    status: CandidateStatus,
    createdAt: Timestamp,
    updatedAt: Timestamp,
    schemaVersion: z.int().positive(),
})
    .meta({ id: 'Candidate' });
//# sourceMappingURL=candidate.js.map